import { Button, Flex, Title } from '@mantine/core';
import { FormTextField, FormRichTextEditor } from '@shared/ui';
import classes from './company-create-form.module.scss';
import { useForm } from '@mantine/form';
import {
  $ValidateCompanyCreate,
  $ValidateCompanyUpdate,
  type CompanyCreateFormValues,
} from './types';
import { INITIAL_VALUES } from './constants';
import { FormCompanyTypesSelect, CompanyNccFormField } from '@entities/company';
import { FormActivitySphereTagsInput } from '@entities/activitySphere';
import { FormEconomicActivityMultiSelect } from '@entities/economic-activity';
import { FormManagerSelect, useGetUsers } from '@entities/user';
import { PaginateType } from '@shared/types';
import { createCustomValidator } from '@shared/utils';
import { useState } from 'react';
import { FormRegionsSelect } from '@entities/region';

type CompanyCreateFormProps = {
  withBorder?: boolean;
  successButtonText?: string;
  handleBack?: () => void;
  handleSubmit: (values: CompanyCreateFormValues) => void;
  initialValues?: CompanyCreateFormValues;
  initialNccVerified?: boolean | null;
  companyId?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  isEdit?: boolean;
};

export const CompanyCreateForm = ({
  withBorder,
  successButtonText = 'Cоздать',
  handleBack,
  handleSubmit,
  initialValues = INITIAL_VALUES,
  initialNccVerified,
  companyId,
  isLoading,
  isDisabled,
  isEdit = false,
}: CompanyCreateFormProps) => {
  const [isManagerHasCompany, setIsManagerHasCompany] = useState(false);

  const form = useForm<CompanyCreateFormValues>({
    initialValues,
    validate: createCustomValidator({
      schema: isEdit ? $ValidateCompanyUpdate : $ValidateCompanyCreate,
      excludeFields: ['manager'],
      customValidators: {
        manager: (value: string | null | undefined) => {
          if (!value) {
            return 'Менеджер обязательно для заполнения';
          }

          if (isManagerHasCompany) {
            return 'Данный менеджер уже прикреплен к другой компании';
          }

          return undefined;
        },
      },
    }),
  });

  const { data } = useGetUsers({
    filter: {
      ['role.name']: 'manager',
    },
    paginate: PaginateType.NONE,
  });

  const handleManagerChange = (value: string | null) => {
    if (!value) {
      form.setFieldValue('manager', null);
      form.setFieldError('manager', null);

      return;
    }

    const findManager = data?.find(item => item.id.toString() === value);

    form.setFieldValue('manager', findManager?.id.toString() || '');

    if (findManager?.company?.id) {
      setIsManagerHasCompany(true);
    }

    if (!findManager?.company?.id) {
      form.setFieldError('manager', null);
    }
  };

  const handleFormSubmit = (values: CompanyCreateFormValues) => {
    handleSubmit(values);
  };

  return (
    <form
      className={withBorder ? classes.formWithBorder : undefined}
      onSubmit={form.onSubmit(handleFormSubmit)}
    >
      <Flex direction='column' gap={32}>
        <Title order={4}>Описание компании</Title>

        <Flex direction='column'>
          <FormTextField
            title='Компания'
            description='Введите название компании'
            placeholder='Введите название'
            key={form.key('name')}
            {...form.getInputProps('name')}
            error={form.errors.name}
          />

          <FormCompanyTypesSelect
            placeholder='Выберите'
            key={form.key('type')}
            {...form.getInputProps('type')}
            error={form.errors.type}
          />

          <CompanyNccFormField
            key={form.key('nif')}
            {...form.getInputProps('nif')}
            error={form.errors.nif}
            nccVerified={initialNccVerified}
            companyId={companyId}
            isNccChanged={form.isDirty('nif')}
          />

          <FormRegionsSelect
            key={form.key('city')}
            placeholder='Выберите'
            {...form.getInputProps('city')}
            error={form.errors.city}
          />

          {/* Todo-Omarov: добавить массив полей для выбора регионов, когда будет реализовано на бэке */}
          {/* <FormArraySelects
            title='Город'
            description='Адрес расположения компании и размещения вакансий'
            placeholder='Выберите'
            selects={[
              {
                name: 'region',
                placeholder: 'Выберите',
                data: ['1', '2', '3'],
              },
              {
                name: 'region',
                placeholder: 'Выберите',
                data: ['1', '2', '3'],
              },
              {
                name: 'region',
                placeholder: 'Выберите',
                data: ['1', '2', '3'],
              },
            ]}
            addSelect={() => {}}
            removeSelect={() => {}}
            addButtonText='Добавить регион'
          /> */}

          <FormManagerSelect
            placeholder='Выберите'
            key={form.key('manager')}
            {...form.getInputProps('manager')}
            onChange={handleManagerChange}
            error={form.errors.manager}
          />

          {isEdit ? (
            <FormEconomicActivityMultiSelect
              placeholder='Выберите'
              key={form.key('economicActivitiesIds')}
              {...form.getInputProps('economicActivitiesIds')}
              error={form.errors.economicActivitiesIds}
            />
          ) : (
            <FormActivitySphereTagsInput
              placeholder='Выберите'
              key={form.key('activitySpheres')}
              {...form.getInputProps('activitySpheres')}
              error={form.errors.activitySpheres}
            />
          )}

          <FormRichTextEditor
            title='Описание компании'
            description='Кратко опишите компанию для кандидатов'
            placeholder='Введите описание'
            key={form.key('description')}
            {...form.getInputProps('description')}
          />
        </Flex>

        <Flex gap={8}>
          <Button variant='gray' onClick={handleBack}>
            Назад
          </Button>
          <Button type='submit' loading={isLoading} disabled={isDisabled}>
            {successButtonText}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};
